export interface CampaignColumn {
    key: string;
    label: string;
    sortKey: string;
    default: boolean;
    group: ColumnGroup;
}

// Picker categories, in the order they're shown. Grouping mirrors how the
// legacy admin laid these out (e.g. Date of Birth sits next to Age) so staff
// aren't scanning a flat 30-item list for a related pair.
export const COLUMN_GROUPS = [
    'Campaign',
    'Fundraising',
    'Client',
    'Location',
    'Medical',
    'Contacts',
] as const;

export type ColumnGroup = (typeof COLUMN_GROUPS)[number];

// All available columns for the Campaign results table.
// Single source of truth shared by CampaignAccordionItem (picker) and
// CampaignSearchResults (table + CSV export).
//
// Declaration order is also the table's left-to-right column order, so the
// default columns stay grouped at the front in their original sequence.
export const ALL_COLUMNS: CampaignColumn[] = [
    // Campaign
    { key: 'campaign_id', label: 'ID', sortKey: 'campaign_id', default: true, group: 'Campaign' },
    { key: 'ntaf_id', label: 'NTAF ID', sortKey: 'ntaf_id', default: true, group: 'Campaign' },
    { key: 'fund_name', label: 'Campaign Name', sortKey: 'fund_name', default: true, group: 'Campaign' },
    { key: 'status', label: 'Status', sortKey: 'status', default: true, group: 'Campaign' },
    // Open Date is the date the campaign went live (funded_date). Staff sort
    // and filter on it constantly, so it earns a default slot (HHLTB-2125).
    { key: 'funded_date', label: 'Open Date', sortKey: 'funded_date', default: true, group: 'Campaign' },

    // Fundraising
    { key: 'raised', label: 'Raised', sortKey: 'raised', default: true, group: 'Fundraising' },
    { key: 'donors', label: 'Donors', sortKey: 'donors', default: true, group: 'Fundraising' },

    // Location
    { key: 'state', label: 'State', sortKey: 'state', default: true, group: 'Location' },

    // Medical
    { key: 'medical_reason', label: 'Medical Reason', sortKey: 'medical_reason', default: true, group: 'Medical' },
    { key: 'medical_issue', label: 'Medical Issue', sortKey: 'medical_issue', default: true, group: 'Medical' },

    // --- optional columns below (default: false) ---

    // Fundraising
    // Goal is off by default: staff read Raised on nearly every run, but a
    // goal only matters on the campaigns they're actively working (HHLTB-2125).
    { key: 'goal', label: 'Goal', sortKey: 'goal', default: false, group: 'Fundraising' },

    // Campaign
    // The real fund name -- region + medical reason, e.g. "Mid-Atlantic
    // Transplant Fund". Calculated per campaign, so there's nothing to sort on.
    { key: 'calculated_fund_name', label: 'Fund Name', sortKey: '', default: false, group: 'Campaign' },
    { key: 'created_date', label: 'Created Date', sortKey: 'created_date', default: false, group: 'Campaign' },
    // Privacy is off by default: most runs don't hinge on it, and it was
    // crowding out columns staff read every time (HHLTB-2125).
    { key: 'privacy', label: 'Privacy', sortKey: 'privacy', default: false, group: 'Campaign' },

    // Client
    // Marketing mail-merges on the parts; a full name can't be split back into
    // them reliably.
    { key: 'first_name', label: 'First Name', sortKey: 'first_name', default: false, group: 'Client' },
    { key: 'middle_name', label: 'Middle Name', sortKey: 'middle_name', default: false, group: 'Client' },
    { key: 'last_name', label: 'Last Name', sortKey: 'last_name', default: false, group: 'Client' },
    { key: 'date_of_birth', label: 'Date of Birth', sortKey: 'date_of_birth', default: false, group: 'Client' },
    { key: 'age', label: 'Age', sortKey: 'age', default: false, group: 'Client' },
    { key: 'deceased', label: 'Deceased', sortKey: 'deceased', default: false, group: 'Client' },
    { key: 'date_of_death', label: 'Date of Death', sortKey: 'date_of_death', default: false, group: 'Client' },
    { key: 'ethnicity', label: 'Ethnicity', sortKey: 'ethnicity', default: false, group: 'Client' },
    { key: 'household_size', label: 'Household Size', sortKey: 'household_size', default: false, group: 'Client' },
    { key: 'single_parent_household', label: 'Single Parent Household', sortKey: 'single_parent_household', default: false, group: 'Client' },
    { key: 'household_income', label: 'Household Income', sortKey: 'household_income', default: false, group: 'Client' },
    { key: 'photo_consented', label: 'Photo Consented', sortKey: 'photo_consented', default: false, group: 'Client' },

    // Location
    { key: 'address', label: 'Address', sortKey: 'address', default: false, group: 'Location' },
    { key: 'region', label: 'Region', sortKey: 'region', default: false, group: 'Location' },
    { key: 'zip_code', label: 'Zip Code', sortKey: 'zip_code', default: false, group: 'Location' },
    { key: 'city', label: 'City', sortKey: 'city', default: false, group: 'Location' },

    // Medical
    { key: 'insurance', label: 'Insurance', sortKey: 'insurance', default: false, group: 'Medical' },
    { key: 'medical_professional', label: 'Medical Professional', sortKey: 'medical_professional', default: false, group: 'Medical' },
    { key: 'medical_center', label: 'Medical Center', sortKey: 'medical_center', default: false, group: 'Medical' },
    { key: 'medical_center_location', label: 'Medical Center Location', sortKey: 'medical_center_location', default: false, group: 'Medical' },

    // Contacts
    { key: 'csc', label: 'CSC', sortKey: 'csc', default: false, group: 'Contacts' },
    { key: 'csc_email', label: 'CSC Email', sortKey: 'csc_email', default: false, group: 'Contacts' },
    { key: 'primary_contact_email', label: 'Primary Contact Email', sortKey: 'primary_contact_email', default: false, group: 'Contacts' },
    { key: 'client_email', label: 'Client Email', sortKey: 'client_email', default: false, group: 'Contacts' },
    { key: 'initial_contact_email', label: 'Initial Contact Email', sortKey: 'initial_contact_email', default: false, group: 'Contacts' },
    { key: 'referred_by', label: 'Referred By', sortKey: 'referred_by', default: false, group: 'Contacts' },
];

// Columns bucketed by group for the pickers, each bucket alphabetized by label
// except Client, where the date/age fields read better in their natural order.
const UNSORTED_GROUPS: ColumnGroup[] = ['Client'];

export const COLUMNS_BY_GROUP: { group: ColumnGroup; columns: CampaignColumn[] }[] =
    COLUMN_GROUPS.map(group => {
        const columns = ALL_COLUMNS.filter(c => c.group === group);
        if (!UNSORTED_GROUPS.includes(group)) {
            columns.sort((a, b) => a.label.localeCompare(b.label));
        }
        return { group, columns };
    }).filter(g => g.columns.length > 0);

// Keys shown by default (matches the previous inline behavior).
export const DEFAULT_VISIBLE_COLUMNS: string[] = ALL_COLUMNS.filter(c => c.default).map(c => c.key);

// Parse a `visible_columns` URL value (comma-joined keys) into a validated key list.
// Returns the default set when the value is missing or yields no valid keys.
export const parseVisibleColumns = (raw: string | null): string[] => {
    if (raw) {
        const valid = new Set(ALL_COLUMNS.map(c => c.key));
        const parsed = raw.split(',').map(s => s.trim()).filter(k => valid.has(k));
        if (parsed.length) return parsed;
    }
    return DEFAULT_VISIBLE_COLUMNS;
};

// True when `selected` is exactly the default set (order-independent).
export const isDefaultSelection = (selected: string[]): boolean =>
    selected.length === DEFAULT_VISIBLE_COLUMNS.length &&
    selected.every(k => DEFAULT_VISIBLE_COLUMNS.includes(k));
